import { useTheme } from "@/contexts/ThemeContext";
import { QrCode, Sun, Moon } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

export default function TermsPage() {
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-background" data-testid="terms-page">
      <SEO
        title="Terms of Service"
        description="Smart-QR terms: acceptable use, subscriptions and billing, content ownership, liability limits, and how we notify you of changes."
        canonicalUrl="https://smart-qr.app/terms"
        ogUrl="https://smart-qr.app/terms"
        ogImage="https://smart-qr.app/icon-192.png"
      />
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/40">
        <div className="flex items-center justify-between px-6 py-4 max-w-4xl mx-auto">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center">
              <QrCode className="text-primary-foreground" size={16} />
            </div>
            <span className="font-heading text-base font-bold">Smart-QR</span>
          </Link>
          <button onClick={toggleTheme} className="p-2.5 rounded-xl hover:bg-secondary text-muted-foreground">
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
      </nav>
      <div className="max-w-3xl mx-auto px-6 py-16 animate-fade-in">
        <h1 className="font-heading text-3xl font-bold mb-2">Terms of Service</h1>
        <p className="text-muted-foreground text-sm mb-10">Last updated: February 2026</p>

        <div className="prose prose-sm max-w-none text-muted-foreground space-y-6 leading-relaxed">
          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground mb-2">1. Acceptance of Terms</h2>
            <p>By creating an account or using Smart-QR, you agree to these Terms of Service. If you do not agree, please do not use the service.</p>
          </section>
          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground mb-2">2. Service Description</h2>
            <p>Smart-QR provides dynamic QR code generation, hosting of public profile pages, link management, and analytics tracking. The service is provided on a subscription basis with free and paid tiers.</p>
          </section>
          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground mb-2">3. User Accounts</h2>
            <p>You are responsible for maintaining the security of your account credentials. You must provide accurate information when creating your account. Each user may only maintain one account.</p>
          </section>
          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground mb-2">4. Acceptable Use</h2>
            <p>You agree not to use Smart-QR for illegal purposes, to distribute malware, to impersonate others, or to post content that violates others' rights. We reserve the right to suspend accounts that violate these terms.</p>
          </section>
          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground mb-2">5. Subscriptions & Payments</h2>
            <p>Paid plans are billed monthly or annually through Stripe. You may cancel your subscription at any time. Refunds are handled on a case-by-case basis. Upon cancellation, your account will remain active until the end of the billing period.</p>
          </section>
          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground mb-2">6. Content Ownership</h2>
            <p>You retain ownership of all content you upload to Smart-QR. By using the service, you grant us a license to host and display your content as part of the service functionality.</p>
          </section>
          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground mb-2">7. Limitation of Liability</h2>
            <p>Smart-QR is provided "as is" without warranties. We are not liable for any indirect, incidental, or consequential damages arising from your use of the service.</p>
          </section>
          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground mb-2">8. Changes to Terms</h2>
            <p>We may update these terms from time to time. We will notify registered users of significant changes via email.</p>
          </section>
          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground mb-2">9. Contact</h2>
            <p>For questions about these terms, contact us at support@smartqr.app.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
